import type {
  Issue,
  SprintStatus,
  Developer,
  BugMetrics,
  TypeMetrics,
  Reporter,
  PrevSprint,
  DashboardData,
  CommandCenterIssue,
  VelocityEntry,
  SprintHealth,
  Assignee,
  CVREntry,
  FlowData,
  BacklogData,
  BugsData,
  CommandCenterData,
  WeekLabel,
  WeekData,
  StaffTotals,
  StaffRow,
  WeeklyProductivityData,
} from '../components/sprint-dashboard/types';
import { BE_ADMIN_BASE } from './environment';
import { apiPost } from './apiClient';

// ─── Constants ───────────────────────────────────────────────────────────────

export const JIRA_PROJECTS = (process.env.REACT_APP_JIRA_PROJECTS || 'SMB,SMA')
  .split(',')
  .map((k) => k.trim())
  .filter(Boolean);

export const JIRA_PROJECT_NAMES = (process.env.REACT_APP_JIRA_PROJECT_NAMES || '')
  .split(',')
  .map((k) => k.trim());

export const AUTO_REFRESH_SECONDS = parseInt(
  process.env.REACT_APP_JIRA_AUTO_REFRESH_SECONDS || '300',
  10
);

const HIGHLIGHT_REPORTERS = (process.env.REACT_APP_JIRA_HIGHLIGHT_REPORTERS || '')
  .split(',')
  .map((k) => k.trim())
  .filter(Boolean);

const STAKEHOLDER_EXTRA_PROJECTS = (
  process.env.REACT_APP_JIRA_STAKEHOLDER_EXTRA_PROJECTS || 'ZAP'
)
  .split(',')
  .map((k) => k.trim())
  .filter(Boolean);

// ─── Status Groups ───────────────────────────────────────────────────────────

const STATUS_GROUPS: Record<string, string[]> = {
  todo: ['To Do'],
  dev: ['In Progress', 'Feature Branch', 'Dev Deployment', 'DEPLOYMENT IN DEV'],
  testing: ['Unit Testing', 'Dev QA', 'CODE REVIEW'],
  uat: ['UAT QA', 'UAT Deployment', 'DEPLOYMENT IN UAT'],
  readyProd: ['Ready for Prod Deployment', 'Ready for Production'],
  production: ['DEPLOYMENT IN PRODUCTON'], // typo intentional — matches Jira config
  done: ['Done'],
};

// "Solved" = fix is deployed to at least Dev environment or beyond.
// Used for bug-resolution metrics in fetchDashboardData.
const BUG_SOLVED_STATUSES: string[] = [
  'Dev Deployment',
  'DEPLOYMENT IN DEV',
  'DEPLOYMENT IN SPRINT BRANCH',
  'Dev QA',
  'CODE REVIEW',
  'Unit Testing',
  'UAT QA',
  'UAT Deployment',
  'DEPLOYMENT IN UAT',
  'Ready for Prod Deployment',
  'Ready for Production',
  'DEPLOYMENT IN PRODUCTON',
  'Done',
];

// ─── Env Stage Map ───────────────────────────────────────────────────────────

// Used by fetchWeeklyProductivity to map a status transition into a deployment
// stage bucket. Order/contents must match the zip prototype.
const ENV_STAGE_MAP: Record<string, string[]> = {
  DEV: [
    'Dev Deployment',
    'DEPLOYMENT IN DEV',
    'Dev QA',
    'CODE REVIEW',
    'Unit Testing',
    'In Progress',
    'Feature Branch',
  ],
  UAT: [
    'UAT QA',
    'UAT Deployment',
    'DEPLOYMENT IN UAT',
    'Ready for Prod Deployment',
    'Ready for Production',
  ],
  PROD: ['Done', 'DEPLOYMENT IN PRODUCTON'],
};

// ─── Auth & Fetch Plumbing ───────────────────────────────────────────────────

// All Jira traffic is routed through the backend-admin proxy. The proxy
// owns the Jira credentials and forwards { path, method, body } to Jira.
async function jiraFetch(
  endpoint: string,
  options?: { method?: string; body?: unknown }
): Promise<unknown> {
  const method = options?.method || 'GET';
  const body = options?.body;

  return apiPost(`${BE_ADMIN_BASE}/jira/proxy`, {
    path: endpoint,
    method,
    body,
  });
}

interface JqlSearchResult {
  key: string;
  fields: Record<string, unknown>;
}

async function jqlSearch(
  query: string,
  fields?: string[],
  maxResults = 500
): Promise<JqlSearchResult[]> {
  try {
    let allIssues: JqlSearchResult[] = [];
    let nextPageToken: string | undefined;

    while (allIssues.length < maxResults) {
      const body: Record<string, unknown> = {
        jql: query,
        maxResults: Math.min(100, maxResults - allIssues.length),
      };
      if (fields) body.fields = fields;
      if (nextPageToken) body.nextPageToken = nextPageToken;

      const data = (await jiraFetch('/rest/api/3/search/jql', {
        method: 'POST',
        body,
      })) as { issues?: Array<{ key: string; fields: Record<string, unknown> }>; nextPageToken?: string } | null;

      const issues: JqlSearchResult[] = (data?.issues || []).map((i) => ({
        key: i.key,
        fields: i.fields,
      }));
      allIssues = allIssues.concat(issues);
      if (!data?.nextPageToken || issues.length === 0) break;
      nextPageToken = data.nextPageToken;
    }

    return allIssues;
  } catch {
    return [];
  }
}

async function agileFetch(path: string): Promise<unknown> {
  try {
    return await jiraFetch(`/rest/agile/1.0${path}`);
  } catch {
    return null;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusGroup(name: string): string {
  const n = (name || '').trim().toLowerCase();
  for (const [group, statuses] of Object.entries(STATUS_GROUPS)) {
    if (statuses.map((s) => s.toLowerCase()).includes(n)) {
      return group;
    }
  }
  return 'todo';
}

function getEnvStage(statusName: string): string | null {
  const n = (statusName || '').trim().toLowerCase();
  for (const [stage, statuses] of Object.entries(ENV_STAGE_MAP)) {
    if (statuses.map((s) => s.toLowerCase()).includes(n)) {
      return stage;
    }
  }
  return null;
}

function getWeekNumber(date: Date): { year: number; week: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNo };
}

function getWeekStart(year: number, week: number): Date {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
  monday.setUTCDate(monday.getUTCDate() + (week - 1) * 7);
  return monday;
}

interface JiraSprint {
  id: number;
  name: string;
  state: string;
  startDate?: string;
  endDate?: string;
}

// ─── Reporter Stats (filtered to highlighted stakeholders only) ──────────────

// Matches the prototype semantics: only configured HIGHLIGHT_REPORTERS show up
// in the "Raised by Stakeholders" UI. Match is case-insensitive substring on
// the issue reporter's displayName.
function computeReporterStats(issues: Issue[], highlightNames: string[]): Reporter[] {
  if (!highlightNames || highlightNames.length === 0) return [];

  interface HLEntry {
    searchKey: string;
    name: string;
    total: number;
    bugs: number;
    tasks: number;
    epics: number;
    matchedName: string | null;
    issues: Issue[];
  }

  const hlEntries: HLEntry[] = highlightNames.map((hl) => ({
    searchKey: hl.toLowerCase().trim(),
    name: hl.trim(),
    total: 0,
    bugs: 0,
    tasks: 0,
    epics: 0,
    matchedName: null,
    issues: [],
  }));

  for (const issue of issues) {
    const reporter = issue.reporter || 'Unknown';
    const reporterLower = reporter.toLowerCase();
    for (const entry of hlEntries) {
      if (reporterLower.includes(entry.searchKey)) {
        entry.total += 1;
        if (entry.matchedName === null) entry.matchedName = reporter;
        const t = (issue.type || '').toLowerCase();
        if (t === 'bug') entry.bugs += 1;
        else if (t === 'epic') entry.epics += 1;
        else entry.tasks += 1;
        entry.issues.push(issue);
        break;
      }
    }
  }

  return hlEntries
    .map<Reporter>((e) => ({
      name: e.matchedName || e.name,
      total: e.total,
      bugs: e.bugs,
      tasks: e.tasks,
      epics: e.epics,
      highlighted: true,
      issues: e.issues,
    }))
    .sort((a, b) => b.total - a.total);
}

// ─── Changelog Fetcher (used by fetchWeeklyProductivity) ────────────────────

interface ChangelogHistoryItem {
  field: string;
  fromString: string | null;
  toString: string | null;
}

interface ChangelogHistory {
  created: string;
  items: ChangelogHistoryItem[];
}

async function fetchChangelog(issueKey: string): Promise<ChangelogHistory[]> {
  try {
    const data = (await jiraFetch(
      `/rest/api/3/issue/${issueKey}/changelog?maxResults=100`
    )) as { values?: ChangelogHistory[] } | null;
    return (data?.values || []) as ChangelogHistory[];
  } catch {
    return [];
  }
}

// ─── Dashboard Data Fetcher ──────────────────────────────────────────────────

export async function fetchDashboardData(): Promise<DashboardData> {
  const keys = JIRA_PROJECTS;
  const pf = `project in (${keys.join(',')})`;

  // Projects scanned for stakeholder reporter coverage (includes ZAP by default).
  const allScanKeys = Array.from(new Set([...keys, ...STAKEHOLDER_EXTRA_PROJECTS]));
  const stakeholderPf = `project in (${allScanKeys.join(',')})`;

  // Phase 1: discover boards + sprints across all scanned projects.
  const sprintMap = new Map<number, JiraSprint>();
  const sprintProjectMap: Record<number, Set<string>> = {};

  for (const key of allScanKeys) {
    const boardRes = (await agileFetch(
      `/board?projectKeyOrId=${key}&maxResults=10`
    )) as { values?: Array<{ id: number }> } | null;
    const boards = boardRes?.values || [];
    for (const board of boards) {
      const [active, closed] = await Promise.all([
        agileFetch(`/board/${board.id}/sprint?state=active&maxResults=10`) as Promise<{
          values?: JiraSprint[];
        } | null>,
        agileFetch(`/board/${board.id}/sprint?state=closed&maxResults=20`) as Promise<{
          values?: JiraSprint[];
        } | null>,
      ]);
      for (const s of active?.values || []) {
        if (!sprintMap.has(s.id)) sprintMap.set(s.id, s);
        if (!sprintProjectMap[s.id]) sprintProjectMap[s.id] = new Set();
        sprintProjectMap[s.id].add(key);
      }
      for (const s of closed?.values || []) {
        if (!sprintMap.has(s.id)) sprintMap.set(s.id, s);
        if (!sprintProjectMap[s.id]) sprintProjectMap[s.id] = new Set();
        sprintProjectMap[s.id].add(key);
      }
    }
  }

  const allSprints = Array.from(sprintMap.values()).sort(
    (a, b) =>
      new Date(a.startDate || 0).getTime() - new Date(b.startDate || 0).getTime()
  );
  const activeSprints = allSprints.filter((s) => s.state === 'active');
  const activeSprint =
    activeSprints.length > 0
      ? activeSprints.reduce((best, s) =>
          new Date(s.endDate || 0).getTime() > new Date(best.endDate || 0).getTime()
            ? s
            : best
        )
      : null;
  const activeSprintIds = activeSprints.map((s) => s.id);
  const closedSprints = allSprints.filter((s) => s.state === 'closed');
  const lastClosed =
    closedSprints.length > 0 ? closedSprints[closedSprints.length - 1] : null;

  const fields = [
    'summary',
    'status',
    'assignee',
    'priority',
    'issuetype',
    'customfield_10016',
    'created',
    'updated',
    'duedate',
    'project',
    'reporter',
  ];

  const sprintJql =
    activeSprintIds.length > 0
      ? `sprint in (${activeSprintIds.join(
          ','
        )}) AND ${pf} ORDER BY assignee ASC, priority DESC`
      : null;
  const stakeholderJql =
    activeSprintIds.length > 0
      ? `sprint in (${activeSprintIds.join(
          ','
        )}) AND ${stakeholderPf} ORDER BY reporter ASC`
      : null;

  // Phase 2: parallel fetch sprint + stakeholder + previous + backlog issues.
  const [currentRaw, stakeholderRaw, prevRaw, backlogRaw] = await Promise.all([
    sprintJql ? jqlSearch(sprintJql, fields, 500) : Promise.resolve([] as JqlSearchResult[]),
    stakeholderJql
      ? jqlSearch(stakeholderJql, fields, 500)
      : Promise.resolve([] as JqlSearchResult[]),
    lastClosed
      ? jqlSearch(
          `sprint = ${lastClosed.id} AND statusCategory = Done AND ${pf} ORDER BY assignee ASC`,
          fields,
          500
        )
      : Promise.resolve([] as JqlSearchResult[]),
    jqlSearch(
      `sprint is EMPTY AND statusCategory != Done AND ${pf} ORDER BY priority DESC`,
      fields,
      500
    ),
  ]);

  const mapIssue = (i: JqlSearchResult): Issue => {
    const f = i.fields;
    const statusName = (f.status as { name?: string })?.name || 'Unknown';
    return {
      key: i.key,
      summary: (f.summary as string) || '',
      status: statusName,
      statusGroup: statusGroup(statusName),
      assignee:
        (f.assignee as { displayName?: string } | null)?.displayName || 'Unassigned',
      reporter:
        (f.reporter as { displayName?: string } | null)?.displayName || 'Unknown',
      sp: (f.customfield_10016 as number) || 0,
      type: (f.issuetype as { name?: string })?.name || 'Task',
      priority: (f.priority as { name?: string })?.name || 'Medium',
      project: (f.project as { key?: string })?.key || '',
      created: (f.created as string) || '',
      updated: (f.updated as string) || '',
    };
  };

  const currentIssues = currentRaw.map(mapIssue);
  const stakeholderIssues = stakeholderRaw.map(mapIssue);
  const prevIssues = prevRaw.map(mapIssue);
  const backlogIssues = backlogRaw.map(mapIssue);

  // ── Backlog by assignee ──
  const backlogByAssignee: Record<string, number> = {};
  for (const issue of backlogIssues) {
    const a = issue.assignee;
    backlogByAssignee[a] = (backlogByAssignee[a] || 0) + 1;
  }

  // ── Sprint status ──
  let sprintStatus: SprintStatus | null = null;
  if (activeSprint && activeSprint.startDate && activeSprint.endDate) {
    const start = new Date(activeSprint.startDate);
    const end = new Date(activeSprint.endDate);
    const now = new Date();
    const totalDays = Math.max(
      1,
      Math.ceil((end.getTime() - start.getTime()) / 86400000)
    );
    const daysRemaining = Math.max(
      0,
      Math.ceil((end.getTime() - now.getTime()) / 86400000)
    );
    const totalSP = currentIssues.reduce((s, i) => s + i.sp, 0);
    const doneSP = currentIssues
      .filter((i) => i.statusGroup === 'done' || i.statusGroup === 'production')
      .reduce((s, i) => s + i.sp, 0);
    const doneCount = currentIssues.filter(
      (i) => i.statusGroup === 'done' || i.statusGroup === 'production'
    ).length;

    const sprintNames = activeSprints.map((s) => s.name);
    sprintStatus = {
      name: sprintNames.length > 1 ? sprintNames.join(' + ') : activeSprint.name,
      startDate: activeSprint.startDate,
      endDate: activeSprint.endDate,
      totalDays,
      daysRemaining,
      totalSP,
      doneSP,
      pctComplete:
        totalSP > 0
          ? Math.round((doneSP / totalSP) * 100)
          : currentIssues.length > 0
          ? Math.round((doneCount / currentIssues.length) * 100)
          : 0,
      totalIssues: currentIssues.length,
    };
  }

  // ── Developers (sprint assignments) ──
  const devMap: Record<string, Developer> = {};
  for (const issue of currentIssues) {
    const a = issue.assignee;
    if (!devMap[a]) devMap[a] = { name: a, issues: [], totalSP: 0 };
    devMap[a].issues.push(issue);
    devMap[a].totalSP += issue.sp;
  }
  const developers: Developer[] = Object.values(devMap).sort(
    (a, b) => b.issues.length - a.issues.length
  );

  // ── Previous sprint summary ──
  let prevSprint: PrevSprint | null = null;
  if (lastClosed) {
    prevSprint = {
      name: lastClosed.name,
      totalIssues: prevIssues.length,
      totalSP: prevIssues.reduce((s, i) => s + i.sp, 0),
      issues: prevIssues,
    };
  }

  // ── Bug metrics (uses BUG_SOLVED_STATUSES — fix deployed to Dev or beyond) ──
  const bugIssues = currentIssues.filter(
    (i) => (i.type || '').toLowerCase() === 'bug'
  );
  const isBugSolved = (issue: Issue): boolean =>
    BUG_SOLVED_STATUSES.includes(issue.status);
  const bugMetrics: BugMetrics = {
    raised: bugIssues.length,
    solved: bugIssues.filter(isBugSolved).length,
    hotfixes: bugIssues.filter(
      (i) =>
        (i.priority || '').toLowerCase() === 'highest' ||
        (i.summary || '').toLowerCase().includes('hotfix')
    ).length,
  };

  // ── Type metrics (done = done OR production status group) ──
  const typeMetrics: TypeMetrics = {
    tasks: { total: 0, solved: 0 },
    epics: { total: 0, closed: 0 },
    bugs: { total: 0, solved: 0 },
  };
  for (const issue of currentIssues) {
    const t = (issue.type || '').toLowerCase();
    if (t === 'bug') {
      typeMetrics.bugs.total += 1;
      if (isBugSolved(issue)) typeMetrics.bugs.solved = (typeMetrics.bugs.solved || 0) + 1;
    } else if (t === 'epic') {
      typeMetrics.epics.total += 1;
      if (issue.statusGroup === 'done' || issue.statusGroup === 'production') {
        typeMetrics.epics.closed = (typeMetrics.epics.closed || 0) + 1;
      }
    } else {
      typeMetrics.tasks.total += 1;
      if (issue.statusGroup === 'done' || issue.statusGroup === 'production') {
        typeMetrics.tasks.solved = (typeMetrics.tasks.solved || 0) + 1;
      }
    }
  }

  // ── Reporter stats (scoped to stakeholder issue set — includes ZAP) ──
  const reporters = computeReporterStats(stakeholderIssues, HIGHLIGHT_REPORTERS);

  // ── Sprint names per project ──
  const sprintNamesByProject: Record<string, string[]> = {};
  for (const s of activeSprints) {
    const projectKeys = sprintProjectMap[s.id]
      ? Array.from(sprintProjectMap[s.id])
      : [];
    for (const pk of projectKeys) {
      if (!sprintNamesByProject[pk]) sprintNamesByProject[pk] = [];
      sprintNamesByProject[pk].push(s.name);
    }
  }

  return {
    sprintStatus,
    issues: currentIssues,
    backlogIssues,
    developers,
    backlogByAssignee,
    bugMetrics,
    typeMetrics,
    reporters,
    prevSprint,
    sprintNamesByProject,
    timestamp: new Date().toISOString(),
  };
}

// ─── Command Center Data Fetcher ─────────────────────────────────────────────

export async function fetchCommandCenterData(): Promise<CommandCenterData> {
  const keys = JIRA_PROJECTS;
  const pf = `project in (${keys.join(',')})`;

  const mapCC = (i: JqlSearchResult): CommandCenterIssue => {
    const f = i.fields;
    const statusObj = f.status as
      | { name?: string; statusCategory?: { key?: string } }
      | null;
    return {
      key: i.key,
      summary: (f.summary as string) || '',
      status: statusObj?.name || 'Unknown',
      statusCat: statusObj?.statusCategory?.key || 'new',
      assignee:
        (f.assignee as { displayName?: string } | null)?.displayName || null,
      priority: (f.priority as { name?: string })?.name || 'Medium',
      type: (f.issuetype as { name?: string })?.name || 'Task',
      sp: (f.customfield_10016 as number) || 0,
      project: (f.project as { key?: string })?.key || '',
      created: (f.created as string) || '',
      updated: (f.updated as string) || '',
      due: (f.duedate as string) || null,
    };
  };

  // Phase 1: discover the first project's board + recent sprints.
  const bd = (await agileFetch(
    `/board?projectKeyOrId=${keys[0]}&type=scrum&maxResults=1`
  )) as { values?: Array<{ id: number }> } | null;
  const boardId = bd?.values?.[0]?.id;

  let allSprints: JiraSprint[] = [];
  if (boardId) {
    const sd = (await agileFetch(
      `/board/${boardId}/sprint?state=active,closed&maxResults=12`
    )) as { values?: JiraSprint[] } | null;
    allSprints = (sd?.values || []).sort(
      (a, b) =>
        new Date(a.startDate || 0).getTime() -
        new Date(b.startDate || 0).getTime()
    );
  }
  const activeSprint = allSprints.find((s) => s.state === 'active') || null;
  const closedSprints = allSprints.filter((s) => s.state === 'closed').slice(-8);

  const full = [
    'summary',
    'status',
    'assignee',
    'priority',
    'issuetype',
    'customfield_10016',
    'created',
    'updated',
    'duedate',
    'project',
    'labels',
  ];
  const minFields = [
    'status',
    'customfield_10016',
    'issuetype',
    'assignee',
    'priority',
  ];

  // Phase 2: parallel fetch all the slices.
  const sprintQueries: Array<Promise<JqlSearchResult[]>> = [
    jqlSearch(
      `sprint in openSprints() AND ${pf} ORDER BY priority DESC`,
      full,
      200
    ),
    jqlSearch(
      `priority = Highest AND sprint in openSprints() AND ${pf} AND statusCategory != Done`,
      full,
      100
    ),
    jqlSearch(
      `due < now() AND statusCategory != Done AND ${pf} ORDER BY due ASC`,
      full,
      100
    ),
    jqlSearch(
      `updated >= -1d AND sprint in openSprints() AND ${pf} ORDER BY updated DESC`,
      full,
      10
    ),
    jqlSearch(
      `sprint is EMPTY AND statusCategory != Done AND ${pf}`,
      ['summary', 'status', 'priority', 'issuetype', 'created'],
      200
    ),
    jqlSearch(`issuetype = Bug AND statusCategory != Done AND ${pf}`, full, 100),
    jqlSearch(`created >= -30d AND ${pf}`, ['created', 'issuetype'], 500),
    jqlSearch(
      `statusCategory = Done AND updated >= -30d AND ${pf}`,
      ['updated', 'issuetype', 'customfield_10016', 'created', 'summary', 'status', 'priority'],
      500
    ),
    jqlSearch(
      `assignee is EMPTY AND sprint in openSprints() AND ${pf}`,
      full,
      100
    ),
    ...closedSprints.map((s) =>
      jqlSearch(`sprint = ${s.id} AND ${pf}`, minFields, 200)
    ),
  ];

  const results = await Promise.all(sprintQueries);
  const [cur, blocked, overdue, recent, backlog, bugs, created, resolved, unassigned] =
    results;
  const sprintResults = results.slice(9);

  const currentIssues = cur.map(mapCC);
  const blockedIssues = blocked.map(mapCC);
  const overdueIssues = overdue.map(mapCC);
  const recentIssues = recent.map(mapCC);
  const unassignedIssues = unassigned.map(mapCC);
  const bugIssues = bugs.map(mapCC);
  const backlogIssues = backlog.map(mapCC);
  const resolvedIssues = resolved.map(mapCC);

  // ── Velocity ──
  const velocity: VelocityEntry[] = closedSprints.map((s, i) => {
    const iss = (sprintResults[i] || []).map(mapCC);
    return {
      name: (s.name || `Sprint ${i + 1}`).replace(/Sprint\s*/i, 'S'),
      committed: iss.reduce((sum, x) => sum + x.sp, 0),
      completed: iss
        .filter((x) => x.statusCat === 'done')
        .reduce((sum, x) => sum + x.sp, 0),
    };
  });
  if (activeSprint) {
    velocity.push({
      name: (activeSprint.name || 'Current').replace(/Sprint\s*/i, 'S'),
      committed: currentIssues.reduce((s, i) => s + i.sp, 0),
      completed: currentIssues
        .filter((i) => i.statusCat === 'done')
        .reduce((s, i) => s + i.sp, 0),
      active: true,
    });
  }
  const cv = velocity.filter((v) => !v.active);
  const avgVelocity =
    cv.length > 0
      ? Math.round(cv.reduce((s, v) => s + v.completed, 0) / cv.length)
      : 0;

  // ── Sprint health ──
  let sprintHealth: SprintHealth | null = null;
  if (activeSprint && activeSprint.startDate && activeSprint.endDate) {
    const start = new Date(activeSprint.startDate);
    const end = new Date(activeSprint.endDate);
    const now = new Date();
    const totalDays = Math.max(
      1,
      Math.ceil((end.getTime() - start.getTime()) / 86400000)
    );
    const daysElapsed = Math.max(
      0,
      Math.ceil((now.getTime() - start.getTime()) / 86400000)
    );
    const daysRemaining = Math.max(0, totalDays - daysElapsed);
    const totalSP = currentIssues.reduce((s, i) => s + i.sp, 0);
    const doneSP = currentIssues
      .filter((i) => i.statusCat === 'done')
      .reduce((s, i) => s + i.sp, 0);
    sprintHealth = {
      name: activeSprint.name,
      totalDays,
      daysElapsed,
      daysRemaining,
      totalSP,
      doneSP,
      remainingSP: totalSP - doneSP,
      pct: totalSP > 0 ? Math.round((doneSP / totalSP) * 100) : 0,
      totalIssues: currentIssues.length,
      doneIssues: currentIssues.filter((i) => i.statusCat === 'done').length,
    };
  }

  // ── Created vs Resolved (4 weeks) ──
  const createdVsResolved: CVREntry[] = [];
  for (let w = 3; w >= 0; w--) {
    const ws = new Date();
    ws.setDate(ws.getDate() - (w + 1) * 7);
    const we = new Date();
    we.setDate(we.getDate() - w * 7);
    createdVsResolved.push({
      label: w === 0 ? 'This wk' : `${w}w ago`,
      created: created.filter((i) => {
        const d = new Date((i.fields.created as string) || 0);
        return d >= ws && d < we;
      }).length,
      resolved: resolved.filter((i) => {
        const d = new Date((i.fields.updated as string) || 0);
        return d >= ws && d < we;
      }).length,
    });
  }

  // ── Assignees breakdown (uses statusCat + status substring matching) ──
  const am: Record<string, Assignee> = {};
  for (const i of currentIssues) {
    const a = i.assignee || 'Unassigned';
    if (!am[a]) {
      am[a] = { name: a, total: 0, sp: 0, todo: 0, active: 0, review: 0, done: 0 };
    }
    am[a].total += 1;
    am[a].sp += i.sp;
    const s = (i.status || '').toLowerCase();
    if (i.statusCat === 'done') am[a].done += 1;
    else if (s.includes('review') || s.includes('qa') || s.includes('test'))
      am[a].review += 1;
    else if (s.includes('progress') || s.includes('dev') || s.includes('feature'))
      am[a].active += 1;
    else am[a].todo += 1;
  }
  const assignees: Assignee[] = Object.values(am).sort((a, b) => b.total - a.total);

  // ── Flow ──
  const flowData: FlowData = { todo: 0, active: 0, review: 0, done: 0 };
  for (const i of currentIssues) {
    const s = (i.status || '').toLowerCase();
    if (i.statusCat === 'done') flowData.done += 1;
    else if (s.includes('review') || s.includes('qa') || s.includes('test'))
      flowData.review += 1;
    else if (s.includes('progress') || s.includes('dev') || s.includes('feature'))
      flowData.active += 1;
    else flowData.todo += 1;
  }

  // ── Cycle times keyed by issue type ──
  const ct: Record<string, number[]> = {};
  for (const i of currentIssues.filter((x) => x.statusCat === 'done')) {
    const days = Math.max(
      1,
      Math.round(
        (new Date(i.updated).getTime() - new Date(i.created).getTime()) / 86400000
      )
    );
    if (!ct[i.type]) ct[i.type] = [];
    ct[i.type].push(days);
  }
  const avgCycleTimes: Record<string, number> = {};
  for (const [t, arr] of Object.entries(ct)) {
    avgCycleTimes[t] = Math.round(arr.reduce((s, v) => s + v, 0) / arr.length);
  }

  // ── Priority breakdown (open only) ──
  const priorityBreakdown: Record<string, number> = {};
  for (const i of currentIssues.filter((x) => x.statusCat !== 'done')) {
    priorityBreakdown[i.priority] = (priorityBreakdown[i.priority] || 0) + 1;
  }

  // ── Bug severity ──
  const severity: Record<string, number> = {};
  for (const i of bugIssues) {
    severity[i.priority] = (severity[i.priority] || 0) + 1;
  }
  const bugsResult: BugsData = {
    total: bugIssues.length,
    severity,
    issues: bugIssues.slice(0, 20),
  };

  // ── Bug resolution time (avg days between created and updated for resolved bugs in 30d) ──
  const bugTimes: number[] = [];
  for (const i of resolvedIssues.filter((x) => (x.type || '').toLowerCase() === 'bug')) {
    const days = Math.max(
      1,
      Math.round(
        (new Date(i.updated).getTime() - new Date(i.created).getTime()) / 86400000
      )
    );
    bugTimes.push(days);
  }
  const bugResolutionTime =
    bugTimes.length > 0
      ? Math.round(bugTimes.reduce((s, v) => s + v, 0) / bugTimes.length)
      : 0;

  // ── Backlog ──
  const backlogData: BacklogData = {
    count: backlogIssues.length,
    issues: backlogIssues.slice(0, 20),
  };

  return {
    velocity,
    avgVelocity,
    sprintHealth,
    issues: currentIssues,
    blocked: blockedIssues,
    overdue: overdueIssues,
    recentlyUpdated: recentIssues,
    unassigned: unassignedIssues,
    backlog: backlogData,
    bugs: bugsResult,
    createdVsResolved,
    assignees,
    priorityBreakdown,
    flowData,
    avgCycleTimes,
    bugResolutionTime,
    timestamp: new Date().toISOString(),
  };
}

// ─── Weekly Productivity Data Fetcher ────────────────────────────────────────

interface StaffWeekSets {
  assigned: Set<string>;
  closed: Set<string>;
  DEV: Set<string>;
  UAT: Set<string>;
  PROD: Set<string>;
}

export async function fetchWeeklyProductivity(
  weeksBack = 4,
  project = 'ALL'
): Promise<WeeklyProductivityData> {
  const keys = JIRA_PROJECTS;
  const projectKeys = project === 'ALL' ? keys : [project];
  const pf = `project in (${projectKeys.join(',')})`;

  const now = new Date();
  const startDate = new Date(now);
  startDate.setDate(startDate.getDate() - weeksBack * 7);
  const startStr = startDate.toISOString().split('T')[0];

  const fields = [
    'summary',
    'status',
    'assignee',
    'priority',
    'issuetype',
    'customfield_10016',
    'created',
    'updated',
    'project',
    'resolutiondate',
  ];

  const query = `${pf} AND updated >= "${startStr}" ORDER BY assignee ASC`;
  const rawIssues = await jqlSearch(query, fields, 1000);

  interface WeeklyIssue {
    key: string;
    summary: string;
    status: string;
    assignee: string;
    type: string;
    priority: string;
    project: string;
    sp: number;
    created: string;
    updated: string;
    resolutionDate: string;
  }

  const issues: WeeklyIssue[] = rawIssues
    .map((i) => {
      const f = i.fields;
      return {
        key: i.key,
        summary: (f.summary as string) || '',
        status: (f.status as { name?: string })?.name || 'Unknown',
        assignee:
          (f.assignee as { displayName?: string } | null)?.displayName ||
          'Unassigned',
        type: (f.issuetype as { name?: string })?.name || 'Task',
        priority: (f.priority as { name?: string })?.name || 'Medium',
        project: (f.project as { key?: string })?.key || '',
        sp: (f.customfield_10016 as number) || 0,
        created: (f.created as string) || '',
        updated: (f.updated as string) || '',
        resolutionDate: (f.resolutiondate as string) || '',
      };
    })
    .filter((i) => i.assignee !== 'Unassigned');

  // Fetch changelogs in parallel batches of 50.
  const changelogMap: Record<string, ChangelogHistory[]> = {};
  const issueKeys = issues.map((i) => i.key);
  for (let i = 0; i < issueKeys.length; i += 50) {
    const batch = issueKeys.slice(i, i + 50);
    const batchResults = await Promise.all(batch.map(fetchChangelog));
    batch.forEach((key, idx) => {
      changelogMap[key] = batchResults[idx];
    });
  }

  // Build week labels (oldest → newest).
  const weeks: WeekLabel[] = [];
  for (let i = weeksBack - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i * 7);
    const { year, week } = getWeekNumber(d);
    const ws = getWeekStart(year, week);
    const we = new Date(ws);
    we.setDate(we.getDate() + 6);
    weeks.push({
      label: `W${week}`,
      weekNum: week,
      year,
      start: ws.toISOString(),
      end: we.toISOString(),
    });
  }

  // Build per-staff, per-week data using Sets for unique-ticket counting.
  const staffWeekData: Record<string, Record<string, StaffWeekSets>> = {};
  const allStaff = new Set<string>();

  const ensureWeek = (assignee: string, label: string): StaffWeekSets => {
    if (!staffWeekData[assignee]) staffWeekData[assignee] = {};
    if (!staffWeekData[assignee][label]) {
      staffWeekData[assignee][label] = {
        assigned: new Set(),
        closed: new Set(),
        DEV: new Set(),
        UAT: new Set(),
        PROD: new Set(),
      };
    }
    return staffWeekData[assignee][label];
  };

  for (const issue of issues) {
    const assignee = issue.assignee;
    allStaff.add(assignee);

    const changelog = changelogMap[issue.key] || [];

    for (const entry of changelog) {
      const created = new Date(entry.created);
      const { year: entryYear, week: entryWeek } = getWeekNumber(created);
      const matchingWeek = weeks.find(
        (w) => w.weekNum === entryWeek && w.year === entryYear
      );
      if (!matchingWeek) continue;

      const wd = ensureWeek(assignee, matchingWeek.label);
      // Any activity on this ticket this week = it was on their plate.
      wd.assigned.add(issue.key);

      for (const item of entry.items || []) {
        if (item.field !== 'status') continue;
        const toStatus = item.toString || '';
        const envStage = getEnvStage(toStatus);
        if (!envStage) continue;

        // Any forward movement counts as closed (reached at least DEV).
        wd.closed.add(issue.key);
        if (envStage === 'DEV') wd.DEV.add(issue.key);
        else if (envStage === 'UAT') wd.UAT.add(issue.key);
        else if (envStage === 'PROD') wd.PROD.add(issue.key);
      }
    }

    // Also check resolution date for tickets resolved in range.
    if (issue.resolutionDate) {
      const rd = new Date(issue.resolutionDate);
      const { year: rYear, week: rWeek } = getWeekNumber(rd);
      const matchingWeek = weeks.find(
        (w) => w.weekNum === rWeek && w.year === rYear
      );
      if (matchingWeek) {
        const wd = ensureWeek(assignee, matchingWeek.label);
        wd.assigned.add(issue.key);
        const envStage = getEnvStage(issue.status);
        if (envStage) {
          wd.closed.add(issue.key);
          if (envStage === 'DEV') wd.DEV.add(issue.key);
          else if (envStage === 'UAT') wd.UAT.add(issue.key);
          else if (envStage === 'PROD') wd.PROD.add(issue.key);
        }
      }
    }
  }

  // ── Build response rows with productivity scores ──
  const staffRows: StaffRow[] = Array.from(allStaff)
    .sort()
    .map((name) => {
      const weekCells: Record<string, WeekData> = {};
      let totalAssigned = 0,
        totalClosed = 0,
        totalDev = 0,
        totalUat = 0,
        totalProd = 0;

      for (const wk of weeks) {
        const raw = staffWeekData[name]?.[wk.label];
        const assigned = raw ? raw.assigned.size : 0;
        const closed = raw ? raw.closed.size : 0;
        const dev = raw ? raw.DEV.size : 0;
        const uat = raw ? raw.UAT.size : 0;
        const prod = raw ? raw.PROD.size : 0;

        const closureRate = assigned > 0 ? Math.round((closed / assigned) * 100) : 0;
        const prodRate = closed > 0 ? Math.round((prod / closed) * 100) : 0;
        const score = Math.round(closureRate * 0.4 + prodRate * 0.6);

        weekCells[wk.label] = {
          assigned,
          closed,
          DEV: dev,
          UAT: uat,
          PROD: prod,
          closureRate,
          prodRate,
          score,
          tickets: {
            DEV: raw ? Array.from(raw.DEV) : [],
            UAT: raw ? Array.from(raw.UAT) : [],
            PROD: raw ? Array.from(raw.PROD) : [],
          },
        };

        totalAssigned += assigned;
        totalClosed += closed;
        totalDev += dev;
        totalUat += uat;
        totalProd += prod;
      }

      const overallClosureRate =
        totalAssigned > 0 ? Math.round((totalClosed / totalAssigned) * 100) : 0;
      const overallProdRate =
        totalClosed > 0 ? Math.round((totalProd / totalClosed) * 100) : 0;
      const overallScore = Math.round(
        overallClosureRate * 0.4 + overallProdRate * 0.6
      );

      const totals: StaffTotals = {
        assigned: totalAssigned,
        closed: totalClosed,
        DEV: totalDev,
        UAT: totalUat,
        PROD: totalProd,
        closureRate: overallClosureRate,
        prodRate: overallProdRate,
        score: overallScore,
      };

      return { name, weeks: weekCells, totals };
    });

  // ── Team totals per week ──
  const teamWeekTotals: Record<string, WeekData> = {};
  for (const wk of weeks) {
    let tAssigned = 0,
      tClosed = 0,
      tDev = 0,
      tUat = 0,
      tProd = 0;
    for (const row of staffRows) {
      const d = row.weeks[wk.label];
      tAssigned += d.assigned;
      tClosed += d.closed;
      tDev += d.DEV;
      tUat += d.UAT;
      tProd += d.PROD;
    }
    const cr = tAssigned > 0 ? Math.round((tClosed / tAssigned) * 100) : 0;
    const pr = tClosed > 0 ? Math.round((tProd / tClosed) * 100) : 0;
    teamWeekTotals[wk.label] = {
      assigned: tAssigned,
      closed: tClosed,
      DEV: tDev,
      UAT: tUat,
      PROD: tProd,
      closureRate: cr,
      prodRate: pr,
      score: Math.round(cr * 0.4 + pr * 0.6),
    };
  }

  // ── Weekly team average score ──
  const staffCount = Math.max(1, staffRows.length);
  const weeklyAverages: Record<string, number> = {};
  for (const wk of weeks) {
    const avgScore =
      staffRows.reduce((s, r) => s + r.weeks[wk.label].score, 0) / staffCount;
    weeklyAverages[wk.label] = Math.round(avgScore);
  }

  return {
    weeks,
    staffRows,
    teamWeekTotals,
    weeklyAverages,
    envStageMap: ENV_STAGE_MAP,
    projects: keys,
    timestamp: new Date().toISOString(),
  };
}
